import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, AlertTriangle, Zap, Crown, Users, 
  CheckCircle, ArrowRight, X, Sparkles
} from 'lucide-react';

export default function UpgradePrompt({ 
  isOpen, 
  onClose, 
  trialStatus,
  canDismiss = true 
}) {
  const navigate = useNavigate();
  
  if (!trialStatus) return null;
  
  const { 
    days_remaining, 
    trial_expired, 
    usage, 
    upgrade_reason,
    needs_upgrade 
  } = trialStatus;

  const getTitle = () => {
    if (trial_expired) {
      return "Your Free Trial Has Ended";
    }
    if (upgrade_reason === 'sermon_limit_reached') {
      return "You've Used All Your Free Sermons";
    }
    if (upgrade_reason === 'series_limit_reached') {
      return "You've Used Your Free Series";
    }
    if (upgrade_reason === 'both_limits_reached') {
      return "You've Reached Your Trial Limits";
    }
    return "Upgrade to Continue";
  };

  const getDescription = () => {
    if (trial_expired) {
      return "Your 7-day free trial has ended. Choose a plan to continue creating sermons and accessing all PulpitPilot AI features.";
    }
    if (upgrade_reason === 'sermon_limit_reached') {
      return `You've created ${usage?.sermons || 3} sermons during your trial. Upgrade to Pro for unlimited sermon generation!`;
    }
    if (upgrade_reason === 'series_limit_reached') {
      return "You've used your free series. Upgrade to Pro to create unlimited sermon series!";
    }
    if (upgrade_reason === 'both_limits_reached') {
      return "You've reached the limits of your free trial. Upgrade now to unlock unlimited sermons and series!";
    }
    return "Upgrade to unlock the full power of PulpitPilot AI.";
  };

  const getIcon = () => {
    if (trial_expired) {
      return <Clock className="w-12 h-12 text-red-500" />;
    }
    return <AlertTriangle className="w-12 h-12 text-amber-500" />;
  };

  const handleUpgrade = (tier) => {
    navigate('/pricing');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={canDismiss ? onClose : undefined}>
      <DialogContent className="max-w-lg bg-white" onPointerDownOutside={canDismiss ? undefined : (e) => e.preventDefault()}>
        {canDismiss && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        
        <div className="text-center py-4">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            {getIcon()}
          </div>
          
          {/* Title */}
          <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">
            {getTitle()}
          </DialogTitle>
          
          {/* Description */}
          <DialogDescription className="text-gray-600 mb-6">
            {getDescription()}
          </DialogDescription>
          
          {/* Trial Stats (if not expired) */}
          {!trial_expired && usage && (
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-2">Your trial usage:</p>
              <div className="flex justify-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {usage.sermons}/{usage.sermons_limit}
                  </p>
                  <p className="text-xs text-gray-500">Sermons</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {usage.series}/{usage.series_limit}
                  </p>
                  <p className="text-xs text-gray-500">Series</p>
                </div>
                {days_remaining !== undefined && days_remaining > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {days_remaining}
                    </p>
                    <p className="text-xs text-gray-500">Days Left</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Upgrade Options */}
          <div className="space-y-3">
            {/* Pro - Recommended */}
            <button
              onClick={() => handleUpgrade('pro')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl p-4 flex items-center justify-between transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Pro Plan</p>
                  <p className="text-sm text-blue-200">Unlimited sermons & series</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold">$17/mo</p>
                <Badge className="bg-amber-400 text-gray-900 text-xs">Popular</Badge>
              </div>
            </button>
            
            {/* Studio */}
            <button
              onClick={() => handleUpgrade('studio')}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl p-4 flex items-center justify-between transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gray-300 p-2 rounded-lg">
                  <Crown className="w-5 h-5 text-gray-700" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Studio Plan</p>
                  <p className="text-sm text-gray-500">Video editing & more</p>
                </div>
              </div>
              <p className="font-bold">$34/mo</p>
            </button>
            
            {/* Church Teams */}
            <button
              onClick={() => handleUpgrade('church_teams')}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-xl p-4 flex items-center justify-between transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="bg-gray-300 p-2 rounded-lg">
                  <Users className="w-5 h-5 text-gray-700" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Church Teams</p>
                  <p className="text-sm text-gray-500">Up to 10 team members</p>
                </div>
              </div>
              <p className="font-bold">$69/mo</p>
            </button>
          </div>
          
          {/* View All Plans Link */}
          <button
            onClick={() => {
              navigate('/pricing');
              onClose();
            }}
            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center justify-center gap-1 mx-auto"
          >
            Compare all plans
            <ArrowRight className="w-4 h-4" />
          </button>
          
          {/* Dismiss option if allowed */}
          {canDismiss && !trial_expired && (
            <button
              onClick={onClose}
              className="mt-4 text-gray-400 hover:text-gray-500 text-sm"
            >
              I'll upgrade later
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
